export interface AccessLike {
  state: 'active' | 'trial' | 'trial_available' | 'none';
  accessEndsAt: string | null;
  currentPlan: { name: string; endsAt: string } | null;
  trial: { minutes: number | null; endsAt: string | null };
}

export interface AccessSummary {
  /** Short name of what the student has. */
  title: string;
  /** One line under it. */
  detail: string;
  tone: 'active' | 'trial' | 'locked';
}

const NZ_DATE = new Intl.DateTimeFormat('en-NZ', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

export function formatDate(iso: string): string {
  return NZ_DATE.format(new Date(iso));
}

export function minutesLeft(endsAt: string, now: number): number {
  return Math.max(0, Math.ceil((new Date(endsAt).getTime() - now) / 60_000));
}

export function formatMinutes(minutes: number): string {
  if (minutes >= 60 && minutes % 60 === 0) {
    const hours = minutes / 60;
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

/** Days left on paid access, rounded up, so "ends today" reads as 1 day. */
export function daysLeft(endsAt: string, now: number): number {
  return Math.max(0, Math.ceil((new Date(endsAt).getTime() - now) / 86_400_000));
}

export function summariseAccess(access: AccessLike, now = Date.now()): AccessSummary {
  switch (access.state) {
    case 'active': {
      const endsAt = access.accessEndsAt ?? access.currentPlan?.endsAt ?? null;
      return {
        title: access.currentPlan?.name ?? 'Active plan',
        detail: endsAt ? `Access ends ${formatDate(endsAt)}` : 'Access is active',
        tone: 'active',
      };
    }
    case 'trial':
      return {
        title: 'Free trial',
        detail: access.trial.endsAt
          ? `${formatMinutes(minutesLeft(access.trial.endsAt, now))} left`
          : 'Running now',
        tone: 'trial',
      };
    case 'trial_available':
      return {
        title: 'Free trial',
        detail: access.trial.minutes
          ? `${formatMinutes(access.trial.minutes)} free. It starts with your first test.`
          : 'Starts with your first test',
        tone: 'trial',
      };
    case 'none':
      return { title: 'No plan', detail: 'Choose a plan to start a test', tone: 'locked' };
  }
}
